import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Row, Col, Modal, Alert } from "react-bootstrap";
import {
  createCategory,
  updateCategory,
  fetchCategoryById,
  clearError,
  clearSuccess,
} from "../../redux/slices/categorySlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";

const CategoryModal = ({ show, handleClose, editId = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!editId;

  // 🌟 सुधारित: Redux state को अलग-अलग चुनें
  const isCreating = useSelector((state) => state.categories.isCreating);
  const isUpdating = useSelector((state) => state.categories.isUpdating);
  const error = useSelector((state) => state.categories.error);
  const success = useSelector((state) => state.categories.success);
  const currentCategory = useSelector((state) => state.categories.currentCategory);
  const categories = useSelector((state) => state.categories.categories) || [];
  // ----------------------------------------------------------------------

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image_url: "",
    status: "Active",
  });

  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (isEditMode && editId && show) {
      if (!currentCategory || currentCategory.id !== editId) {
        dispatch(fetchCategoryById(editId));
      }
    }
  }, [dispatch, editId, isEditMode, show, currentCategory]);

  useEffect(() => {
    if (isEditMode && currentCategory && currentCategory.id === editId) {
      setFormData({
        name: currentCategory.name || "",
        description: currentCategory.description || "",
        image_url: currentCategory.image_url || "",
        status: currentCategory.status || "Active",
      });
    }
  }, [currentCategory, isEditMode, editId]);

  useEffect(() => {
    if (show) {
      if (!isEditMode) {
        resetForm();
      }
    } else {
      resetForm();
    }
  }, [show, isEditMode]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      image_url: "",
      status: "Active",
    });
    setImageFile(null);
    setValidationErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      if (!/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        e.target.value = "";
        return;
      }

      // Mandatory Dimension Validation: 2200 x 2000 Px
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width !== 2200 || img.height !== 2000) {
          toast.error(`Image dimensions must be exactly 2200 x 2000 pixels. (Detected: ${img.width}x${img.height})`);
          setImageFile(null);
          e.target.value = "";
        } else {
          setImageFile(file);
        }
        URL.revokeObjectURL(objectUrl);
      };
      img.onerror = () => {
        toast.error("Failed to load image for validation");
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
      };
      img.src = objectUrl;
    } else {
      setImageFile(null);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Category name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      errors.name = "Only alphabetic characters and spaces are allowed";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters long";
    }
    if (formData.description && formData.description.length > 500) {
      errors.description = "Description must not exceed 500 characters";
    }
    // Image is required in both create and update modes
    if (!imageFile && !formData.image_url) {
      errors.image = "Image is required";
    } else if (imageFile) {
      if (!imageFile.type.startsWith("image/")) {
        errors.image = "Please select a valid image file";
      } else if (imageFile.size > 5 * 1024 * 1024) {
        errors.image = "Image must be less than 5MB";
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }



    try {
      setIsUploading(true);

      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status,
      };

      if (isEditMode) {
        if (!imageFile) {
          submitData.image_url = formData.image_url || null;
        }
        await dispatch(
          updateCategory({
            id: editId,
            categoryData: submitData,
            imageFile,
          })
        ).unwrap();
        // Toast will be shown by useEffect in Category.jsx based on Redux success state
      } else {
        await dispatch(
          createCategory({
            categoryData: submitData,
            imageFile,
          })
        ).unwrap();
        // Toast will be shown by useEffect in Category.jsx based on Redux success state
      }

      // Close modal immediately - parent will handle toast and refetch
      handleModalClose();
    } catch (err) {
      console.error("Category operation failed:", err);
      toast.error(err || "Something went wrong");
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    dispatch(clearError());
    // Don't clear success here - let parent component handle it and show toast
    // The parent will clear success after showing the toast
    handleClose();
  };

  const isSubmitting = isCreating || isUpdating || isUploading;

  return (
    <>
      <Modal show={show} onHide={handleModalClose} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {isEditMode ? `Update Category` : "Create Category"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => dispatch(clearError())}
              >
                {error}
              </Alert>
            )}

            <Row className="mb-3">
              <Col md={6} className="ps-0">
                <Form.Group>
                  <Form.Label>
                    Category Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Enter category name"
                    value={formData.name}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.name}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} className="pe-0">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12} className="p-0">
                <Form.Group>
                  <Form.Label>
                    Category Image <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="mb-2 p-2 rounded bg-danger-soft border border-danger">
                    <p className="text-danger fw-bold mb-0 small">
                      <i className="fa-solid fa-triangle-exclamation me-2"></i>
                      IMPORTANT NOTE: Image must be exactly 2200 x 2000 pixels.
                    </p>
                  </div>
                  <Form.Control
                    id="file-uplad"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    disabled={isSubmitting}
                    isInvalid={!!validationErrors.image}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.image}
                  </Form.Control.Feedback>

                  {(imageFile || formData.image_url) && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-3">
                        <img
                          src={
                            imageFile
                              ? URL.createObjectURL(imageFile)
                              : formData.image_url
                          }
                          alt="Preview"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />

                        <div className="flex-grow-1">
                          <strong>
                            {imageFile ? imageFile.name : "Current Image"}
                          </strong>
                          {isEditMode && !imageFile && (
                            <div className="text-muted small">
                              Existing image will be kept
                            </div>
                          )}
                        </div>

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRemoveImage}
                          disabled={isSubmitting}
                        >
                          <FaRegTrashAlt />
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                placeholder="Enter category description (optional)"
                value={formData.description}
                onChange={handleInputChange}
                isInvalid={!!validationErrors.description}
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.description}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                {formData.description.length}/500 characters
              </Form.Text>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              className="btn-outline-danger-custom"
              onClick={handleModalClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="btn-outline-success"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Processing..."
                : isEditMode
                ? "Update Category"
                : "Create Category"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default CategoryModal;
